/**
 * Centralized query key factory for React Query.
 * Ensures consistent cache keys across the application.
 *
 * Usage:
 * - queryKeys.inventory.all - Base key for all inventory queries
 * - queryKeys.inventory.list(filters) - List with specific filters
 * - queryKeys.inventory.detail(id) - Single item detail
 *
 * Benefits:
 * - Type-safe query keys
 * - Easy cache invalidation
 * - Consistent naming patterns
 */

export const queryKeys = {
  // Inventory Module
  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.inventory.lists(), filters] as const,
    details: () => [...queryKeys.inventory.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inventory.details(), id] as const,
    levels: (itemId: string) => [...queryKeys.inventory.all, 'levels', itemId] as const,
    batches: (itemId: string) => [...queryKeys.inventory.all, 'batches', itemId] as const,
  },

  // Shipments Module
  shipments: {
    all: ['shipments'] as const,
    lists: () => [...queryKeys.shipments.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.shipments.lists(), filters] as const,
    details: () => [...queryKeys.shipments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.shipments.details(), id] as const,
    tracking: (id: string) => [...queryKeys.shipments.all, 'tracking', id] as const,
    milestones: (id: string) => [...queryKeys.shipments.all, 'milestones', id] as const,
  },

  // Purchase Orders Module
  purchaseOrders: {
    all: ['purchaseOrders'] as const,
    lists: () => [...queryKeys.purchaseOrders.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.purchaseOrders.lists(), filters] as const,
    details: () => [...queryKeys.purchaseOrders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.purchaseOrders.details(), id] as const,
    withShipments: (id: string) => [...queryKeys.purchaseOrders.all, 'withShipments', id] as const,
  },

  // Equipment Module
  equipment: {
    all: ['equipment'] as const,
    lists: () => [...queryKeys.equipment.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.equipment.lists(), filters] as const,
    details: () => [...queryKeys.equipment.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.equipment.details(), id] as const,
    hierarchy: () => [...queryKeys.equipment.all, 'hierarchy'] as const,
    maintenance: (id: string) => [...queryKeys.equipment.all, 'maintenance', id] as const,
  },

  // Work Orders Module
  workOrders: {
    all: ['workOrders'] as const,
    lists: () => [...queryKeys.workOrders.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.workOrders.lists(), filters] as const,
    details: () => [...queryKeys.workOrders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workOrders.details(), id] as const,
    lineItems: (id: string) => [...queryKeys.workOrders.all, 'lineItems', id] as const,
  },

  // Suppliers Module
  suppliers: {
    all: ['suppliers'] as const,
    lists: () => [...queryKeys.suppliers.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.suppliers.lists(), filters] as const,
    details: () => [...queryKeys.suppliers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.suppliers.details(), id] as const,
  },

  // Locations Module
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.locations.lists(), filters] as const,
    details: () => [...queryKeys.locations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.locations.details(), id] as const,
  },

  // Consumption Module
  consumption: {
    all: ['consumption'] as const,
    lists: () => [...queryKeys.consumption.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.consumption.lists(), filters] as const,
    details: () => [...queryKeys.consumption.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.consumption.details(), id] as const,
    variance: (filters: object) => [...queryKeys.consumption.all, 'variance', filters] as const,
  },

  // Maintenance Schedules Module
  maintenanceSchedules: {
    all: ['maintenanceSchedules'] as const,
    lists: () => [...queryKeys.maintenanceSchedules.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.maintenanceSchedules.lists(), filters] as const,
    details: () => [...queryKeys.maintenanceSchedules.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.maintenanceSchedules.details(), id] as const,
  },

  // Certificates Module
  certificates: {
    all: ['certificates'] as const,
    lists: () => [...queryKeys.certificates.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.certificates.lists(), filters] as const,
    details: () => [...queryKeys.certificates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.certificates.details(), id] as const,
  },

  // Goods Receipt Module
  goodsReceipts: {
    all: ['goodsReceipts'] as const,
    lists: () => [...queryKeys.goodsReceipts.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.goodsReceipts.lists(), filters] as const,
    details: () => [...queryKeys.goodsReceipts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.goodsReceipts.details(), id] as const,
  },

  // Users Module
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
  },

  // Roles Module
  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.roles.lists(), filters] as const,
    details: () => [...queryKeys.roles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.roles.details(), id] as const,
  },

  // Companies Module
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
  },

  // Alerts Module
  alerts: {
    all: ['alerts'] as const,
    lists: () => [...queryKeys.alerts.all, 'list'] as const,
    list: (filters: object) => [...queryKeys.alerts.lists(), filters] as const,
    unread: () => [...queryKeys.alerts.all, 'unread'] as const,
  },

  // Analytics Module
  analytics: {
    all: ['analytics'] as const,
    dashboard: () => [...queryKeys.analytics.all, 'dashboard'] as const,
    inventory: (filters: object) => [...queryKeys.analytics.all, 'inventory', filters] as const,
    equipment: (filters: object) => [...queryKeys.analytics.all, 'equipment', filters] as const,
  },

  // TMS Module
  tms: {
    carriers: {
      all: ['tms', 'carriers'] as const,
      lists: () => [...queryKeys.tms.carriers.all, 'list'] as const,
      list: (filters: object) => [...queryKeys.tms.carriers.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.tms.carriers.all, 'detail', id] as const,
    },
    rates: {
      all: ['tms', 'rates'] as const,
      lists: () => [...queryKeys.tms.rates.all, 'list'] as const,
      list: (filters: object) => [...queryKeys.tms.rates.lists(), filters] as const,
    },
    rfq: {
      all: ['tms', 'rfq'] as const,
      lists: () => [...queryKeys.tms.rfq.all, 'list'] as const,
      list: (filters: object) => [...queryKeys.tms.rfq.lists(), filters] as const,
      detail: (id: string) => [...queryKeys.tms.rfq.all, 'detail', id] as const,
    },
    tracking: {
      all: ['tms', 'tracking'] as const,
      shipment: (id: string) => [...queryKeys.tms.tracking.all, 'shipment', id] as const,
    },
  },
};

// Type helper for extracting query key types
export type QueryKeys = typeof queryKeys;
